/**
 * Resource Gallery Configurations
 *
 * This file contains the configuration objects for each resource type
 * used by the useResourceGallery hook and related components.
 */

import { Tracker } from "@/models/tracker";
import { Enrichment } from "@/models/enrichment";
import { Alert, AlertType } from "@/models/alert";
import { Dashboard, DashboardPopulated } from "@/models/dashboard";
import { Investigation } from "@/models/investigation";
import { Conversation } from "@/models/conversation";
import { Report, ReportStatus, ReportType } from "@/api/report";
import { WidgetWithContext } from "@/hooks/use-widgets";
import {
  ResourceGalleryConfig,
  GroupByOption,
} from "@/hooks/use-resource-gallery";
import { getTrackerValue, getTrackerSource } from "@/utils/tracker-helpers";
import { getTypeLabel as getEnrichmentTypeLabel } from "@/features/enrichments/utils";
import { getWidgetTypeLabel } from "@/hooks/use-widgets";

// =============================================================================
// Tracker Configuration
// =============================================================================

export const TRACKER_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "source", label: "Source" },
  { value: "tags", label: "Tags" },
  { value: "status", label: "Statut" },
  { value: "type", label: "Type" },
];

export const trackerGalleryConfig: ResourceGalleryConfig<Tracker> = {
  resourceType: "tracker",
  storageKey: "tracker",
  defaultGroupBy: "source",
  defaultViewMode: "board",
  groupByOptions: TRACKER_GROUP_BY_OPTIONS,

  getId: (tracker) => tracker.id,

  getDisplayName: (tracker) => {
    const trackerValue = getTrackerValue(tracker);
    return trackerValue?.value || tracker.id;
  },

  getGroupKey: (tracker, groupBy) => {
    switch (groupBy) {
      case "source":
        return getTrackerSource(tracker);
      case "tags":
        // Returns array of tags for multi-group assignment
        const tags = tracker.tags || [];
        return tags.length > 0 ? (tags as unknown as string) : "Sans tags";
      case "status":
        if (tracker.is_import) return "Import";
        return tracker.status === "active" ? "Actifs" : "Inactifs";
      case "type":
        return tracker.is_import ? "Import" : "Monitoring";
      default:
        return "";
    }
  },

  filterFn: (tracker, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const trackerValue = getTrackerValue(tracker);
    return (
      tracker.id.toLowerCase().includes(term) ||
      (trackerValue?.value?.toLowerCase().includes(term) ?? false)
    );
  },

  sortGroups: (a, b) => {
    // Put special groups at the end
    if (a === "Sans tags" || a === "Autre") return 1;
    if (b === "Sans tags" || b === "Autre") return -1;
    if (a === "Import") return 1;
    if (b === "Import") return -1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Enrichment Configuration
// =============================================================================

export const ENRICHMENT_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun" },
  { value: "type", label: "Type" },
  { value: "status", label: "Statut" },
];

export const enrichmentGalleryConfig: ResourceGalleryConfig<Enrichment> = {
  resourceType: "enrichment",
  storageKey: "enrichment",
  defaultGroupBy: "status",
  defaultViewMode: "board",
  groupByOptions: ENRICHMENT_GROUP_BY_OPTIONS,

  getId: (enrichment) => enrichment.id,

  getDisplayName: (enrichment) => {
    return (
      enrichment.name ||
      getEnrichmentTypeLabel(enrichment.type) ||
      enrichment.id
    );
  },

  getGroupKey: (enrichment, groupBy) => {
    switch (groupBy) {
      case "type":
        return getEnrichmentTypeLabel(enrichment.type) || "Autre";
      case "status":
        if (enrichment.status === "active") return "Actifs";
        if (enrichment.status === "error") return "Erreur";
        return "Inactifs";
      default:
        return "";
    }
  },

  filterFn: (enrichment, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const typeLabel = getEnrichmentTypeLabel(enrichment.type) || "";
    return (
      enrichment.name?.toLowerCase().includes(term) ||
      typeLabel.toLowerCase().includes(term) ||
      enrichment.description?.toLowerCase().includes(term) ||
      false
    );
  },

  sortGroups: (a, b) => {
    // Put "Actifs" first, "Erreur" last for status grouping
    if (a === "Actifs") return -1;
    if (b === "Actifs") return 1;
    if (a === "Erreur") return 1;
    if (b === "Erreur") return -1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Alert Configuration
// =============================================================================

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  new_documents: "Nouveaux documents",
  threshold: "Seuil",
  change: "Changement",
  anomaly: "Anomalie",
  custom: "Personnalisé",
};

export const ALERT_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun" },
  { value: "type", label: "Type" },
  { value: "status", label: "Statut" },
];

export const alertGalleryConfig: ResourceGalleryConfig<Alert> = {
  resourceType: "alert",
  storageKey: "alert",
  defaultGroupBy: "status",
  defaultViewMode: "grid",
  groupByOptions: ALERT_GROUP_BY_OPTIONS,

  getId: (alert) => alert.id,

  getDisplayName: (alert) => alert.name,

  getGroupKey: (alert, groupBy) => {
    switch (groupBy) {
      case "type":
        return ALERT_TYPE_LABELS[alert.config.type] || "Autre";
      case "status":
        return alert.enabled ? "Activées" : "Désactivées";
      default:
        return "";
    }
  },

  filterFn: (alert, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const typeLabel = ALERT_TYPE_LABELS[alert.config.type] || "";
    return (
      alert.name?.toLowerCase().includes(term) ||
      alert.description?.toLowerCase().includes(term) ||
      typeLabel.toLowerCase().includes(term) ||
      false
    );
  },

  sortGroups: (a, b) => {
    // Put "Activées" first for status grouping
    if (a === "Activées") return -1;
    if (b === "Activées") return 1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Dashboard Configuration
// =============================================================================

export const DASHBOARD_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "recent", label: "Par date" },
  { value: "widgets_count", label: "Par nombre de widgets" },
];

export const dashboardGalleryConfig: ResourceGalleryConfig<Dashboard | DashboardPopulated> = {
  resourceType: "dashboard",
  storageKey: "dashboard",
  defaultGroupBy: "none",
  defaultViewMode: "grid",
  groupByOptions: DASHBOARD_GROUP_BY_OPTIONS,

  getId: (dashboard) => dashboard.id!,

  getDisplayName: (dashboard) => {
    const metaName = dashboard.metadata?.name as string | undefined;
    return dashboard.name || metaName || "Dashboard sans nom";
  },

  getGroupKey: (dashboard, groupBy) => {
    switch (groupBy) {
      case "recent": {
        if (!dashboard.updated_at) return "Sans date";
        const date = new Date(dashboard.updated_at);
        const now = new Date();
        const diffDays = Math.floor(
          (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
        );
        if (diffDays === 0) return "Aujourd'hui";
        if (diffDays === 1) return "Hier";
        if (diffDays <= 7) return "Cette semaine";
        if (diffDays <= 30) return "Ce mois";
        return "Plus ancien";
      }
      case "widgets_count": {
        const count = 'widgets' in dashboard ? dashboard.widgets?.length || 0 : 0;
        if (count === 0) return "Vide";
        if (count <= 3) return "1-3 widgets";
        if (count <= 6) return "4-6 widgets";
        return "7+ widgets";
      }
      default:
        return "";
    }
  },

  filterFn: (dashboard, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const metaName = dashboard.metadata?.name as string | undefined;
    const metaDesc = dashboard.metadata?.description as string | undefined;
    const name = dashboard.name || metaName || "";
    const description = dashboard.description || metaDesc || "";
    return (
      name.toLowerCase().includes(term) ||
      description.toLowerCase().includes(term)
    );
  },

  sortGroups: (a, b) => {
    // Put time-based groups in order
    const order = [
      "Aujourd'hui",
      "Hier",
      "Cette semaine",
      "Ce mois",
      "Plus ancien",
      "Sans date",
    ];
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;

    // Put widget count groups in order
    const widgetOrder = ["Vide", "1-3 widgets", "4-6 widgets", "7+ widgets"];
    const wIndexA = widgetOrder.indexOf(a);
    const wIndexB = widgetOrder.indexOf(b);
    if (wIndexA !== -1 && wIndexB !== -1) return wIndexA - wIndexB;

    return a.localeCompare(b);
  },
};

// =============================================================================
// Widget Configuration
// =============================================================================

export const WIDGET_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "type", label: "Par type" },
  { value: "dashboard", label: "Par dashboard" },
];

export const widgetGalleryConfig: ResourceGalleryConfig<WidgetWithContext> = {
  resourceType: "widget",
  storageKey: "widget",
  defaultGroupBy: "none",
  defaultViewMode: "grid",
  groupByOptions: WIDGET_GROUP_BY_OPTIONS,

  getId: (widget) => widget.id,

  getDisplayName: (widget) => {
    return (
      widget.widget.metadata?.name ||
      widget.widget.name ||
      getWidgetTypeLabel(widget.widget.chart)
    );
  },

  getGroupKey: (widget, groupBy) => {
    switch (groupBy) {
      case "type":
        return getWidgetTypeLabel(widget.widget.chart);
      case "dashboard":
        return widget.dashboardName || "Sans dashboard";
      default:
        return "";
    }
  },

  filterFn: (widget, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const name = widget.widget.metadata?.name || widget.widget.name || "";
    const type = getWidgetTypeLabel(widget.widget.chart);
    const dashboardName = widget.dashboardName || "";
    return (
      name.toLowerCase().includes(term) ||
      type.toLowerCase().includes(term) ||
      dashboardName.toLowerCase().includes(term)
    );
  },

  sortGroups: (a, b) => {
    if (a === "Sans dashboard") return 1;
    if (b === "Sans dashboard") return -1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Investigation Configuration
// =============================================================================

export const INVESTIGATION_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "status", label: "Statut" },
];

export const investigationGalleryConfig: ResourceGalleryConfig<Investigation> =
  {
    resourceType: "investigation",
    storageKey: "investigation",
    defaultGroupBy: "none",
    defaultViewMode: "grid",
    groupByOptions: INVESTIGATION_GROUP_BY_OPTIONS,

    getId: (investigation) => investigation.id,

    getDisplayName: (investigation) => {
      return investigation.name || "Sans nom";
    },

    getGroupKey: (investigation, groupBy) => {
      switch (groupBy) {
        case "status":
          return investigation.status === "active" ? "Actives" : "Inactives";
        default:
          return "";
      }
    },

    filterFn: (investigation, searchTerm) => {
      const term = searchTerm.toLowerCase();
      return (
        investigation.name?.toLowerCase().includes(term) ||
        investigation.description?.toLowerCase().includes(term) ||
        false
      );
    },

    sortGroups: (a, b) => {
      if (a === "Actives") return -1;
      if (b === "Actives") return 1;
      return a.localeCompare(b);
    },
  };

// =============================================================================
// Conversation Configuration
// =============================================================================

export const CONVERSATION_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "status", label: "Statut" },
];

export const conversationGalleryConfig: ResourceGalleryConfig<Conversation> = {
  resourceType: "conversation",
  storageKey: "conversation",
  defaultGroupBy: "none",
  defaultViewMode: "list",
  groupByOptions: CONVERSATION_GROUP_BY_OPTIONS,

  getId: (conversation) => conversation.id!,

  getDisplayName: (conversation) => {
    return conversation.name || "Sans titre";
  },

  getGroupKey: (conversation, groupBy) => {
    switch (groupBy) {
      case "status":
        if (conversation.pinned) return "Épinglées";
        if (conversation.archived) return "Archivées";
        return "Actives";
      default:
        return "";
    }
  },

  filterFn: (conversation, searchTerm) => {
    const term = searchTerm.toLowerCase();
    return (
      conversation.name?.toLowerCase().includes(term) ||
      conversation.description?.toLowerCase().includes(term) ||
      false
    );
  },

  sortGroups: (a, b) => {
    // Put "Épinglées" first, "Archivées" last
    if (a === "Épinglées") return -1;
    if (b === "Épinglées") return 1;
    if (a === "Archivées") return 1;
    if (b === "Archivées") return -1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Report Configuration
// =============================================================================

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  widget: "Widget",
  dashboard: "Dashboard",
  text: "Text",
};

const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  pending: "En attente",
  generating: "En cours",
  generated: "Généré",
  failed: "Échec",
};

export const REPORT_GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "none", label: "Aucun groupement" },
  { value: "type", label: "Type" },
  { value: "status", label: "Statut" },
];

export const reportGalleryConfig: ResourceGalleryConfig<Report> = {
  resourceType: "report",
  storageKey: "report",
  defaultGroupBy: "none",
  defaultViewMode: "list",
  groupByOptions: REPORT_GROUP_BY_OPTIONS,

  getId: (report) => report.id,

  getDisplayName: (report) => {
    return report.name || "Sans nom";
  },

  getGroupKey: (report, groupBy) => {
    switch (groupBy) {
      case "type":
        return REPORT_TYPE_LABELS[report.report_type] || "Autre";
      case "status":
        return REPORT_STATUS_LABELS[report.status] || "Inconnu";
      default:
        return "";
    }
  },

  filterFn: (report, searchTerm) => {
    const term = searchTerm.toLowerCase();
    const typeLabel = REPORT_TYPE_LABELS[report.report_type] || "";
    return (
      report.name?.toLowerCase().includes(term) ||
      report.description?.toLowerCase().includes(term) ||
      typeLabel.toLowerCase().includes(term) ||
      false
    );
  },

  sortGroups: (a, b) => {
    // Put "Généré" first for status grouping
    if (a === "Généré") return -1;
    if (b === "Généré") return 1;
    if (a === "Échec") return 1;
    if (b === "Échec") return -1;
    return a.localeCompare(b);
  },
};

// =============================================================================
// Helper to get config by resource type
// =============================================================================

export type ResourceConfigMap = {
  tracker: typeof trackerGalleryConfig;
  enrichment: typeof enrichmentGalleryConfig;
  alert: typeof alertGalleryConfig;
  dashboard: typeof dashboardGalleryConfig;
  widget: typeof widgetGalleryConfig;
  investigation: typeof investigationGalleryConfig;
  conversation: typeof conversationGalleryConfig;
  report: typeof reportGalleryConfig;
};

export const resourceGalleryConfigs: ResourceConfigMap = {
  tracker: trackerGalleryConfig,
  enrichment: enrichmentGalleryConfig,
  alert: alertGalleryConfig,
  dashboard: dashboardGalleryConfig,
  widget: widgetGalleryConfig,
  investigation: investigationGalleryConfig,
  conversation: conversationGalleryConfig,
  report: reportGalleryConfig,
};
